import React, { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { Search, ChevronDown, ChevronRight, Check, Loader2, X } from 'lucide-react';

/**
 * M2 类目树选择器（支持多选/单选）
 *
 * Props:
 *   value           - 多选: JSON array string "[1,2]"; 单选: single ID "3"
 *   onChange(val)   - 多选: JSON array string "[1,2]"; 单选: single ID
 *   channelStoreId  - M2 store ID for fetching categories
 *   singleSelect    - 单选模式（默认 false）
 *   viewOnly        - Read-only mode
 *   t               - Translation function
 */
const M2CategorySelector = ({ value, onChange, channelStoreId, singleSelect = false, viewOnly = false, t = (s) => s, displayName = '' }) => {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const loadedOnceRef = useRef(false);
  const dropdownRef = useRef(null);

  // Parse current value as array of selected IDs
  // Value is a JSON array string like "[1,2]" or a single ID like "3"
  const selectedIds = React.useMemo(() => {
    if (!value || value === '' || value === '0' || value === '[]') return [];
    // Try parsing as JSON array first (e.g., "[1,2]")
    try {
      const parsed = JSON.parse(String(value));
      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch (e) {
      // Not JSON, fall through
    }
    if (singleSelect) return [String(value)];
    // Legacy comma-separated fallback
    return String(value).split(',').map(v => v.trim()).filter(Boolean);
  }, [value, singleSelect]);

  // 加载类目树
  const hasValue = selectedIds.length > 0;
  useEffect(() => {
    if (loadedOnceRef.current) return;

    const shouldLoad = singleSelect || isOpen || hasValue;
    if (!shouldLoad || !channelStoreId) return;

    loadedOnceRef.current = true;
    setLoading(true);

    // Use cached channel_category endpoint for fast loading
    fetch(`/api/channel/categories?store_id=${channelStoreId}`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success' && Array.isArray(res.data?.categories)) {
          setCategories(res.data.categories.map(c => ({
            id: c.m2_category_id,
            parent_id: c.parent_id,
            name: c.name,
            is_active: c.is_active,
            position: c.position,
            level: c.level,
          })));
        } else {
          setCategories([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setCategories([]);
        setLoading(false);
    });
  }, [isOpen, channelStoreId, singleSelect, viewOnly, hasValue]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build parent→children index (O(1) child lookup)
  // For M2 categories, root nodes have parent_id pointing to non-existent id (e.g., 1)
  const childrenMap = useMemo(() => {
    const map = {};
    const idSet = new Set(categories.map(c => c.id));
    categories.forEach(c => {
      const pid = idSet.has(c.parent_id) ? c.parent_id : '__root__';
      (map[pid] ??= []).push(c);
    });
    return map;
  }, [categories]);

  // Build id→parent_id index (O(1) ancestor tracing)
  const parentMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.parent_id; });
    return map;
  }, [categories]);

  // Build id→category index (O(1) category lookup)
  const idMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c; });
    return map;
  }, [categories]);

  // Deferred search: keeps UI responsive during typing
  const deferredSearch = useDeferredValue(searchQuery);

  // Memoized search matching (runs on deferred value, not every keystroke)
  const searchMatchIds = useMemo(() => {
    if (!deferredSearch || !deferredSearch.trim()) return new Set();
    const q = deferredSearch.toLowerCase();
    const matching = new Set();
    for (const c of categories) {
      if ((c.name || '').toLowerCase().includes(q)) {
        matching.add(c.id);
        let pid = c.parent_id;
        while (pid) {
          matching.add(pid);
          pid = parentMap[pid] ?? null;
        }
      }
    }
    return matching;
  }, [deferredSearch, categories, parentMap]);

  const toggleExpand = (catId) => {
    setExpanded(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleSelect = (catId) => {
    if (singleSelect) {
      const idStr = String(catId);
      if (selectedIds[0] === idStr) {
        onChange('0');
      } else {
        onChange(idStr);
      }
      setIsOpen(false);
      return;
    }
    const idStr = String(catId);
    const newIds = selectedIds.includes(idStr)
      ? selectedIds.filter(id => id !== idStr)
      : [...selectedIds, idStr];
    onChange(JSON.stringify(newIds.map(Number)));
  };

  const getDisplayName = (cat) => cat.name || '';

  // Iterative path builder using idMap O(1·depth)
  const getPath = (catId) => {
    const result = [];
    let currentId = catId;
    while (currentId) {
      const cat = idMap[currentId];
      if (!cat) break;
      result.unshift(cat);
      currentId = cat.parent_id;
    }
    return result;
  };

  /** 获取选中类目的名称，找不到时返回 null（绝不回退到 ID） */
  const getSelectedName = (id) => {
    if (!categories.length) return null;
    const cat = idMap[parseInt(id)];
    return cat ? cat.name : null;
  };

  // Render tree
  const renderTree = (parentId = null, level = 0) => {
    const children = childrenMap[parentId ?? '__root__'] || [];
    if (children.length === 0) return null;

    const isSearching = !!searchQuery;
    const matchMap = searchMatchIds;

    return children.map(c => {
      if (isSearching && !matchMap.has(c.id)) return null;

      const hasKids = (childrenMap[c.id]?.length ?? 0) > 0;
      const isExpanded = expanded[c.id] || false;
      const autoExpand = isSearching && hasKids && (childrenMap[c.id] || []).some(ch =>
        matchMap.has(ch.id)
      );
      const isSelected = selectedIds.includes(String(c.id));

      if (isSearching) {
        const isDirectMatch = matchMap.has(c.id);
        const path = getPath(c.id);
        return (
          <div key={c.id}>
            {isDirectMatch && (
              <div
                className={`flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                  isSelected ? 'bg-blue-100 dark:bg-blue-900/50' : ''
                }`}
                onClick={() => toggleSelect(c.id)}
              >
                <div className="shrink-0 mr-1.5 w-4 h-4 border border-slate-300 dark:border-slate-500 rounded flex items-center justify-center">
                  {isSelected && <Check size={12} className="text-blue-600 dark:text-blue-400" />}
                </div>
                <span className="truncate">
                  {path.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 && <span className="text-slate-300 dark:text-slate-600 mx-1">›</span>}
                      <span className={p.id === c.id ? 'font-medium' : 'text-slate-500 dark:text-slate-400'}>
                        {getDisplayName(p)}
                      </span>
                    </span>
                  ))}
                </span>
              </div>
            )}
            {(autoExpand || isExpanded) && renderTree(c.id, level + 1)}
          </div>
        );
      }

      return (
        <div key={c.id}>
          <div
            className={`flex items-center p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer text-sm transition-colors ${
              isSelected ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
            }`}
            style={{ paddingLeft: `${level * 14 + 8}px` }}
          >
            <span
              className="shrink-0 w-5 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
              onClick={(e) => { e.stopPropagation(); hasKids && toggleExpand(c.id); }}
            >
              {hasKids ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="text-xs">•</span>}
            </span>
            <div
              className="shrink-0 ml-1.5 w-4 h-4 border border-slate-300 dark:border-slate-500 rounded flex items-center justify-center"
              onClick={() => toggleSelect(c.id)}
            >
              {isSelected && <Check size={12} className="text-blue-600 dark:text-blue-400" />}
            </div>
            <span
              className={`ml-1.5 truncate ${isSelected ? 'font-bold' : ''}`}
              onClick={() => toggleSelect(c.id)}
            >
              {getDisplayName(c)}
            </span>
          </div>
          {isExpanded && renderTree(c.id, level + 1)}
        </div>
      );
    });
  };

  // View-only: show selected category names
  if (viewOnly) {
    const displayNames = categories.length > 0
      ? categories
          .filter(c => selectedIds.includes(String(c.id)))
          .map(c => getDisplayName(c))
          .join(', ')
      : null;
    return (
      <div className="px-1 py-2">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {displayNames || (typeof t === 'function' ? t('eav.no_category') : '(none)')}
        </span>
      </div>
    );
  }



  const removeCategory = (e, catId) => {
    e.stopPropagation();
    const idStr = String(catId);
    if (singleSelect) {
      onChange('0');
    } else {
      const newIds = selectedIds.filter(id => id !== idStr);
      onChange(JSON.stringify(newIds.map(Number)));
    }
  };



  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer flex justify-between items-center bg-white dark:bg-slate-800"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1 min-w-0">
          {singleSelect ? (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {displayName || (typeof t === 'function' ? t('eav.select_category') : 'Select category...')}
            </span>
          ) : selectedIds.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedIds.map((id, idx) => {
                const catName = getSelectedName(id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full"
                  >
                    <span className="truncate max-w-[120px]">{catName || `ID:${id}`}</span>
                    <button
                      type="button"
                      className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                      onClick={(e) => removeCategory(e, id)}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {typeof t === 'function' ? t('eav.select_category') : 'Select categories...'}
            </span>
          )}
        </div>
        <ChevronDown size={16} className="text-slate-400 dark:text-slate-500 shrink-0 ml-1" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden max-h-80 flex flex-col">
          <div className="p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={14} />
              <input
                className="w-full pl-8 pr-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                placeholder={typeof t === 'function' ? t('common.search') : 'Search...'}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto p-1 flex-1">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-4 text-xs text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{typeof t === 'function' ? t('common.loading') : 'Loading...'}</span>
              </div>
            ) : categories.length > 0 ? (
              renderTree()
            ) : (
              <div className="p-4 text-center text-xs text-slate-400">No categories found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default M2CategorySelector;
